import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Gt2ApiService } from '../../../api/gt2-api.service';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { fuseAnimations } from '../../../../../@fuse/animations/index';
import { FuseTranslationLoaderService } from '../../../../../@fuse/services/translation-loader.service';
import { Gt2LocaleService } from '../../../../localization/gt2-locale.service';
import { AuthenticationService } from '../../../api/auth-service.service';
import { AddressComponent } from '../../../components/address/address.component';
import { ContactComponent } from '../../../components/contact/contact.component';
import { RolesService } from '../../../services/roles.service';
import { Settings } from '../../../settings/settings';
import { GT2FormPageAbstract } from '../../abstract/GT2FormPageAbstract';
import { CrudOrganizationService } from './crud-organization.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';
import { GT2DateAdapter } from '../../../utils/GT2DateAdapter';
import { Title } from '@angular/platform-browser';
import { ModulesService } from '../../../services/modules.service';
import { MATERIAL_COLOR_PALETTE } from '../../../const/MaterialColorPalette';
import { AppRoutes } from '../../../const/AppRoutes';

@Component({
  selector: 'app-crud-organization',
  templateUrl: './crud-organization.component.html',
  styleUrls: ['./crud-organization.component.scss'],
  providers: [{ provide: DateAdapter, useClass: GT2DateAdapter }],
  animations: fuseAnimations,
})
export class CrudOrganizationComponent
  extends GT2FormPageAbstract
  implements OnInit, OnDestroy
{
  @ViewChild('addressComponent') addressComponent?: AddressComponent;
  @ViewChild('contactComponent') contactComponent?: ContactComponent;

  orgUUID!: string;
  shopUUID!: string;
  organization: any;
  organizationForm: UntypedFormGroup;
  override loading: boolean = false;
  editMode: boolean = false;
  isVisible: boolean = true;
  organizationName?: string;
  colorPalette = MATERIAL_COLOR_PALETTE;
  emailBackgroundColor: string = '';
  private paletteObserver?: MutationObserver;
  private emailColorSaveTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private auth: AuthenticationService,
    private logger: NGXLogger,
    private formBuilder: UntypedFormBuilder,
    private http: HttpClient,
    private dialog: MatDialog,
    public translate: TranslateService,
    private translationLoader: FuseTranslationLoaderService,
    private toastr: ToastrService,
    private gt2LocaleService: Gt2LocaleService,
    private api: Gt2ApiService,
    public location: Location,
    public rolesService: RolesService,
    private route: ActivatedRoute,
    public modulesService: ModulesService,
    private titleService: Title,
    public router: Router,
    private orgService: CrudOrganizationService,
    private elementRef: ElementRef,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);

    this.organizationForm = this.formBuilder.group({
      name: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
          Validators.required,
        ],
      ],
      website: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      facebook_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      twitter_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      youtube_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      linkedin_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      instagram_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      tiktok_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      whatsapp_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      pinterest_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      threads_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      telegram_url: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      support_email: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
      support_tel: [
        '',
        [
          Validators.minLength(Settings.inputMinChar),
          Validators.maxLength(Settings.inputMaxChar),
        ],
      ],
    });
  }

  ngOnInit() {
    // this.titleService.setTitle(this.translate.instant('GENERIC.ORGANIZATION'));

    this.route.params.subscribe({
      next: (params: any) => {
        this.orgUUID = params.id;
        this.shopUUID = params.shop_uuid;
        this.loadOrgByUUID(this.orgUUID);
      },
    });

    // Reposition the ngx-colors palette panel so it is not clipped.
    this.paletteObserver = new MutationObserver(() => {
      this.repositionColorPalette();
    });
    this.paletteObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  ngOnDestroy() {
    if (this.paletteObserver) {
      this.paletteObserver.disconnect();
    }
    if (this.emailColorSaveTimer) {
      clearTimeout(this.emailColorSaveTimer);
    }
  }

  private repositionColorPalette(): void {
    const palette: HTMLElement | null = document.querySelector(
      'ngx-colors-panel .opened',
    );
    if (!palette) {
      return;
    }
    const swatch: HTMLElement | null =
      this.elementRef.nativeElement.querySelector(
        'ngx-colors .app-color-picker',
      );
    if (!swatch) {
      return;
    }

    const swatchRect = swatch.getBoundingClientRect();
    const paletteHeight = palette.offsetHeight;
    const viewportHeight = window.innerHeight;

    palette.style.transform = 'none';
    palette.style.position = 'fixed';
    palette.style.left = swatchRect.left + 'px';

    const spaceAbove = swatchRect.top;
    const spaceBelow = viewportHeight - swatchRect.bottom;

    if (spaceAbove >= paletteHeight) {
      palette.style.top = swatchRect.top - paletteHeight + 'px';
    } else if (spaceBelow >= paletteHeight) {
      palette.style.top = swatchRect.bottom + 'px';
    } else {
      palette.style.top = Math.max(0, viewportHeight - paletteHeight) + 'px';
    }
  }

  public setEmailBackgroundColor(color: string): void {
    if (!this.organization) {
      return;
    }
    this.organization.email_background_color = color;
    this.emailBackgroundColor = color;
    // The picker emits an event per character while a hex value is typed.
    // Debounce the save so the API is only called once the user stops
    // changing the value (i.e. the hex code is complete).
    if (this.emailColorSaveTimer) {
      clearTimeout(this.emailColorSaveTimer);
    }
    this.emailColorSaveTimer = setTimeout(() => {
      this.emailColorSaveTimer = undefined;
      this.autosaveDataChange(
        { email_background_color: color },
        this.translate.instant('ORG.EMAIL_BG_COLOR_SAVED'),
      );
    }, 600);
  }

  public clearEmailBackgroundColor(): void {
    if (!this.organization) {
      return;
    }
    if (this.emailColorSaveTimer) {
      clearTimeout(this.emailColorSaveTimer);
      this.emailColorSaveTimer = undefined;
    }
    this.organization.email_background_color = null;
    this.emailBackgroundColor = '';
    this.autosaveDataChange(
      { email_background_color: null },
      this.translate.instant('ORG.EMAIL_BG_COLOR_SAVED'),
    );
  }

  public loadOrgByUUID(uuid: string): void {
    this.loading = true;
    this.orgService.loadOrgByUUID(uuid).subscribe({
      next: (organization) => {
        if (organization.data) {
          this.organization = organization.data;
          this.organizationName = this.organization.name;
          this.emailBackgroundColor =
            this.organization.email_background_color || '';
          // this.titleService.setTitle(
          //   this.translate.instant('GENERIC.ORGANIZATION') +
          //     ' ' +
          //     this.organization.name,
          // );
        }
        this.loading = false;
      },
      error: (error) => {
        this.logger.error(
          'CrudOrganizationComponent.loadOrgByUUID() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.handleAPIError(error, this.dialog, null, null, this.router);
      },
    });
  }

  public override autosaveDataChange(data: any, successMessage?: string): void {
    this.loading = true;
    this.orgService.updateOrganization(data, this.orgUUID).subscribe({
      next: (response) => {
        this.organization = response.data;
        this.organizationName = this.organization.name;
        this.emailBackgroundColor =
          this.organization.email_background_color || '';
        this.loading = false;
        if (successMessage) {
          this.toastr.success(successMessage);
        }
      },
      error: (error) => {
        this.logger.error(
          'CrudOrganizationComponent.autosaveDataChange() -> ERROR: ' +
            JSON.stringify(error),
        );
        this.loading = false;
        this.handleAPIError(
          error,
          this.dialog,
          this.toastr,
          this.translate.instant('GENERIC.ERRORS.GENERIC') + ' CO001',
          this.router,
        );
      },
    });
  }

  public onManualSaveOrganization(): void {
    if (this.organizationForm.dirty) {
      this.autosaveDataChange(this.organization);
    }
  }

  /**
   * Saves the bilingual rich-text signature. The editor is not a form control,
   * so this saves the whole organization regardless of `organizationForm` state.
   */
  public onSaveSignature(): void {
    this.autosaveDataChange(this.organization);
  }

  public onEdit($event: any): void {
    this.editMode = !this.editMode;

    if (this.addressComponent) {
      this.addressComponent.editMode = this.editMode;
    }

    if (this.contactComponent) {
      this.contactComponent.editMode = this.editMode;
    }

    if (this.editMode) {
    } else {
      this.organizationForm.markAsPristine();
      this.organizationForm.markAsUntouched();
    }
  }

  public onOpenInNewTab(): void {
    this.openInNewTab(this.router.url);
  }

  public onManageTaxNumbers(): void {
    this.router.navigateByUrl(
      '/' + this.shopUUID + '/' + AppRoutes.SETTINGS + '/taxes',
    );
  }

}
